/* ------------------------------------------------------------------------------------------------------------------
 * Address entry shared by the portal and the console.
 *  - useZipAutofill: a valid ZIP / postal code fills city and state (which stay editable), offers the other city
 *    names the ZIP covers, and says so gently when the ZIP is unknown. The apps pass the lookup (their /v1 client).
 *  - AddressAutocomplete: the street field as an accessible combobox (ARIA 1.2 pattern: arrow keys, Enter,
 *    Escape, a polite result count). Suggestions come through our API (the Google key never reaches the browser),
 *    with one session token per search so Google bills one session. Falls back to a plain field when the API
 *    says autocomplete is off, or when no source is given.
 *  - BookingAddressFields: the full address for one end at booking, prefilled from the quote, ZIP fixed.
 * ---------------------------------------------------------------------------------------------------------------- */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { nextOptionIndex, useFloating, usePortalNeed } from "./components.js";
import { TextField } from "./controls.js";

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

/* ---------------------------------------------------------------- ZIP autofill */

export type PostalPlace = { city: string; state: string; alternatives: string[] };
/** The app's ZIP lookup (GET /v1/geo/postal/:code). Resolve null for an unknown code; reject on network errors. */
export type PostalLookup = (code: string, country: string) => Promise<PostalPlace | null>;
export type ZipAutofill = {
  status: "idle" | "looking" | "found" | "unknown";
  /** The ZIP's primary city first, then its other names. */
  cities: string[];
  /** Use one of `cities`: still counts as filled from the ZIP, so a later ZIP change replaces it. */
  pickCity: (city: string) => void;
};

/** A code complete enough to look up: 5-digit ZIP (ZIP+4 ok) or a Canadian FSA / full postal code. */
export function lookupReady(code: string, country: string): boolean {
  const c = code.trim().toUpperCase();
  if (country === "US") return /^\d{5}(-?\d{4})?$/.test(c);
  if (country === "CA") return /^[A-Z]\d[A-Z]( ?\d[A-Z]\d)?$/.test(c);
  return false;
}

type CityState = { city: string; region: string; postalCode: string; country: string };

/**
 * Fill city and state from the ZIP as it's typed. Only fills a field that is empty or still holds what the last
 * lookup put there, so a city the person typed is never overwritten.
 */
export function useZipAutofill<V extends CityState>(value: V, onChange: (v: V) => void, lookup: PostalLookup | undefined, debounceMs = 180): ZipAutofill {
  const [state, setState] = useState<{ status: ZipAutofill["status"]; cities: string[] }>({ status: "idle", cities: [] });
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };
  /** What this hook last put in each field; a field holding anything else belongs to the person. */
  const auto = useRef<{ city: string | null; region: string | null }>({ city: null, region: null });
  const seq = useRef(0);
  const code = value.postalCode.trim().toUpperCase();
  const country = value.country;
  useEffect(() => {
    const mine = ++seq.current;
    if (!lookup || !lookupReady(code, country)) {
      setState((s) => (s.status === "idle" ? s : { status: "idle", cities: [] }));
      return;
    }
    setState({ status: "looking", cities: [] });
    const t = setTimeout(() => {
      lookup(code, country).then(
        (place) => {
          if (mine !== seq.current) return;
          if (!place) {
            setState({ status: "unknown", cities: [] });
            return;
          }
          const { value: cur, onChange: emit } = latest.current;
          const cityFree = !cur.city.trim() || cur.city === auto.current.city;
          const regionFree = !cur.region.trim() || cur.region === auto.current.region;
          const next = { ...cur, city: cityFree ? place.city : cur.city, region: regionFree ? place.state : cur.region };
          auto.current = { city: cityFree ? place.city : auto.current.city, region: regionFree ? place.state : auto.current.region };
          if (next.city !== cur.city || next.region !== cur.region) emit(next);
          setState({ status: "found", cities: [place.city, ...place.alternatives.filter((a) => a !== place.city)] });
        },
        () => {
          if (mine === seq.current) setState({ status: "idle", cities: [] });
        },
      );
    }, debounceMs);
    return () => clearTimeout(t);
  }, [code, country, lookup, debounceMs]);
  const pickCity = (city: string) => {
    auto.current = { ...auto.current, city };
    const { value: cur, onChange: emit } = latest.current;
    emit({ ...cur, city });
  };
  return { ...state, pickCity };
}

/** "Also: Los Angeles · West Hollywood" under the city: the other names the ZIP goes by, one click to use. */
export function ZipCityChoices({ cities, current, onPick }: { cities: string[]; current: string; onPick: (city: string) => void }) {
  const others = cities.filter((c) => c.toLowerCase() !== current.trim().toLowerCase());
  if (cities.length < 2 || others.length === 0) return null;
  return (
    <span className="ff-zipalts">
      <span className="ff-zipalts__k">This ZIP also covers</span>
      {others.map((c) => (
        <button key={c} type="button" className="ff-zipalts__btn" onClick={() => onPick(c)} aria-label={`Use ${c} as the city`}>
          {c}
        </button>
      ))}
    </span>
  );
}

export const ZIP_UNKNOWN = "We don't know this ZIP. Type the city and state.";

/* ---------------------------------------------------------------- street autocomplete */

export type AddressSuggestion = { placeId: string; text: string; main: string; secondary: string };
export type PickedAddress = { line1: string; line2: string; city: string; state: string; postalCode: string; country: "US" | "CA" | null };
/** The app's calls to our API: POST /v1/geo/autocomplete and GET /v1/geo/place/:id. */
export type AddressSource = {
  suggest: (input: string, sessionToken: string) => Promise<{ enabled: boolean; suggestions: AddressSuggestion[] }>;
  details: (placeId: string, sessionToken: string) => Promise<PickedAddress>;
};

/** Google session token: URL-safe, at most 36 characters. One per search, ended by the details call. */
export function newSessionToken(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  let s = "";
  while (s.length < 32) s += Math.random().toString(36).slice(2);
  return s.slice(0, 32);
}

export type AddressAutocompleteProps = {
  label: string;
  value: string;
  onChange: (text: string) => void;
  /** A suggestion was chosen and resolved to its parts. */
  onPick: (address: PickedAddress) => void;
  /** Omit (or let the API answer `enabled: false`) for a plain text field. */
  source?: AddressSource;
  error?: ReactNode;
  hint?: ReactNode;
  optional?: boolean;
  example?: string;
  className?: string;
  id?: string;
  minChars?: number;
  debounceMs?: number;
  autoComplete?: string;
};

export function AddressAutocomplete({ label, value, onChange, onPick, source, error, hint, optional, example, className, id, minChars = 3, debounceMs = 250, autoComplete = "address-line1" }: AddressAutocompleteProps) {
  const auto = useId();
  const inputId = id ?? `a${auto.replace(/:/g, "")}`;
  const listId = `${inputId}-list`;
  const [off, setOff] = useState(!source);
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [active, setActive] = useState(-1);
  const [failed, setFailed] = useState(false);
  const token = useRef<string | null>(null);
  const seq = useRef(0);
  const controlRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const live = !off && !!source;

  // Only what the person types searches; a value set by a pick or a prefill never does.
  useEffect(() => {
    const q = (query ?? "").trim();
    const mine = ++seq.current;
    if (!live || q.length < minChars) {
      setOptions([]);
      setBusy(false);
      return;
    }
    setBusy(true);
    const t = setTimeout(() => {
      token.current ??= newSessionToken();
      source!.suggest(q, token.current).then(
        (r) => {
          if (mine !== seq.current) return;
          if (!r.enabled) {
            setOff(true);
            setOpen(false);
          }
          setFailed(false);
          setOptions(r.suggestions);
          setActive(r.suggestions.length ? 0 : -1);
          setBusy(false);
        },
        () => {
          if (mine !== seq.current) return;
          setOptions([]);
          setFailed(true);
          setBusy(false);
        },
      );
    }, debounceMs);
    return () => clearTimeout(t);
  }, [query, live, source, minChars, debounceMs]);

  const choose = (o: AddressSuggestion) => {
    const t = token.current ?? newSessionToken();
    token.current = null; // the details call ends this session
    seq.current++;
    setOpen(false);
    setOptions([]);
    setQuery(null);
    onChange(o.main);
    source!.details(o.placeId, t).then(onPick, () => undefined);
  };

  const expanded = live && open && (query ?? "").trim().length >= minChars && (busy || options.length > 0 || failed);
  const { style: listStyle, side } = useFloating(expanded, controlRef, listRef, { side: "bottom", align: "start", gap: 6, matchWidth: true });
  const portal = usePortalNeed(expanded, controlRef);


  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!options.length) return;
      e.preventDefault();
      setOpen(true);
      setActive((a) => nextOptionIndex(e.key, a, options.length));
    } else if (e.key === "Enter" && expanded && active >= 0 && options[active]) {
      e.preventDefault();
      choose(options[active]);
    } else if (e.key === "Escape" && expanded) {
      e.preventDefault();
      setOpen(false);
    }
  };
  const desc = error ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined;
  const list = (
    <ul ref={listRef} id={listId} role="listbox" aria-label="Suggested addresses" className={cx("ffc-menu ffx-combo__list ff-addrac__list", listStyle.position && "is-floating")} data-side={side} style={listStyle} hidden={!expanded}>
      {busy && options.length === 0 ? <li className="ffx-combo__status" role="presentation">Searching…</li> : null}
      {failed && !busy ? <li className="ffx-combo__status" role="presentation">Suggestions aren't available. Type the address.</li> : null}
      {options.map((o, i) => (
        <li
          key={o.placeId}
          id={`${listId}-${i}`}
          role="option"
          aria-selected={i === active}
          data-highlighted={i === active ? "" : undefined}
          className="ffc-opt ffx-combo__opt"
          onMouseDown={(e) => {
            e.preventDefault();
            choose(o);
          }}
          onMouseMove={() => setActive(i)}
        >
          <span className="ffx-combo__label">{o.main}</span>
          {o.secondary ? <span className="ffx-combo__detail">{o.secondary}</span> : null}
        </li>
      ))}
      {options.length > 0 ? <li className="ff-addrac__credit" role="presentation">Suggestions by Google</li> : null}
    </ul>
  );
  return (
    <div className={cx("ffc-field", live && "ffx-combo ff-addrac", error ? "is-invalid" : null, className)}>
      <div className="ffc-box ffx-combo__control" ref={controlRef}>
        <input
          id={inputId}
          className="ffc-input ffx-combo__input"
          // One input in both modes, so focus survives the API saying autocomplete is off mid-typing.
          {...(live
            ? {
                role: "combobox",
                autoComplete: "off",
                "aria-expanded": expanded,
                "aria-controls": listId,
                "aria-autocomplete": "list" as const,
                "aria-busy": busy || undefined,
                "aria-activedescendant": expanded && active >= 0 && options[active] ? `${listId}-${active}` : undefined,
              }
            : { autoComplete })}
          spellCheck={false}
          aria-required={optional ? undefined : true}
          aria-invalid={error ? true : undefined}
          aria-describedby={desc}
          placeholder=" "
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={live ? onKey : undefined}
          onBlur={() => setOpen(false)}
          onFocus={() => setOpen(true)}
        />
        <label htmlFor={inputId} className="ffc-label">
          {label}
          {optional ? <span className="ffc-optional"> Optional</span> : null}
        </label>
        {example ? <span className="ffc-example" aria-hidden="true">{example}</span> : null}
        {busy ? <span className="ffx-combo__busy" aria-hidden="true" /> : null}
        {!live ? null : portal && expanded ? createPortal(list, document.body) : list}
      </div>
      <p className="ffc-msg ffc-msg--error" id={`${inputId}-err`} aria-live="polite">
        {error || null}
      </p>
      {!error && hint ? (
        <p className="ffc-msg" id={`${inputId}-hint`}>
          {hint}
        </p>
      ) : null}
      <span className="ffx-sr" aria-live="polite">
        {live && expanded && !busy ? `${options.length} ${options.length === 1 ? "suggestion" : "suggestions"}` : ""}
      </span>
    </div>
  );
}

/* ---------------------------------------------------------------- booking: the full address for one end */

export type BookingAddressValue = { companyName: string; line1: string; line2: string; city: string; region: string };

/**
 * One end of the shipment at booking: company, street (with suggestions), line 2, city and state, all prefilled from
 * the quote. The ZIP is the quoted one and can't change here; a picked address in another ZIP is flagged so the
 * person re-quotes instead of booking the wrong lane.
 */
export function BookingAddressFields({ prefix, legend, value, onChange, errors, postalCode, country, source, note, className, children }: {
  prefix: string;
  legend: ReactNode;
  value: BookingAddressValue;
  onChange: (v: BookingAddressValue) => void;
  errors: Partial<Record<string, string | undefined>>;
  postalCode: string;
  country: string;
  source?: AddressSource;
  note?: ReactNode;
  className?: string;
  /** This end's contact (name, phone, email), shown inside the same box under the address. */
  children?: ReactNode;
}) {
  const [elsewhere, setElsewhere] = useState<string | null>(null);
  const set = <K extends keyof BookingAddressValue>(k: K, v: BookingAddressValue[K]) => onChange({ ...value, [k]: v });
  const sameZip = (a: string) => a.replace(/[\s-]/g, "").toUpperCase().slice(0, country === "US" ? 5 : 6) === postalCode.replace(/[\s-]/g, "").toUpperCase().slice(0, country === "US" ? 5 : 6);
  const pick = (a: PickedAddress) => {
    if (a.postalCode && !sameZip(a.postalCode)) {
      // Not this lane: clear the street so the booking can't go out with an address the quote didn't rate.
      setElsewhere(a.postalCode);
      onChange({ ...value, line1: "" });
      return;
    }
    setElsewhere(null);
    onChange({ ...value, line1: a.line1 || value.line1, line2: a.line2 || value.line2, city: a.city || value.city, region: a.state || value.region });
  };
  const zipLabel = country === "US" ? "ZIP" : "Postal code";
  return (
    <fieldset className={cx("ff-fieldset ff-bookaddr", className)}>
      <legend>{legend}</legend>
      {note ? <p className="ff-bookaddr__note">{note}</p> : null}
      <div className="ff-bookaddr__grid">
        <TextField className="ff-bookaddr__company" label="Company" hint="Printed on the BOL" autoComplete="organization" aria-required error={errors[`${prefix}.companyName`]} value={value.companyName} onChange={(e) => set("companyName", e.target.value)} />
        <AddressAutocomplete
          className="ff-bookaddr__street"
          label="Street address"
          value={value.line1}
          onChange={(t) => {
            setElsewhere(null);
            set("line1", t);
          }}
          onPick={pick}
          source={source}
          error={elsewhere ? `That address is in ${zipLabel} ${elsewhere}, but this quote is for ${postalCode}. Pick an address in ${postalCode}, or go back and re-quote.` : errors[`${prefix}.line1`]}
        />
        <TextField className="ff-bookaddr__line2" label="Suite, unit or dock" optional autoComplete="address-line2" error={errors[`${prefix}.line2`]} value={value.line2} onChange={(e) => set("line2", e.target.value)} />
        <TextField className="ff-bookaddr__city" label="City" aria-required autoComplete="address-level2" error={errors[`${prefix}.city`]} value={value.city} onChange={(e) => set("city", e.target.value)} />
        <TextField className="ff-bookaddr__region ff-qf--caps" label={country === "CA" ? "Province" : "State"} aria-required maxLength={2} autoComplete="address-level1" error={errors[`${prefix}.region`]} value={value.region} onChange={(e) => set("region", e.target.value.toUpperCase())} />
        <p className="ff-bookaddr__zip">
          <span className="ff-bookaddr__zipk">{zipLabel}</span>
          <span className="ff-bookaddr__zipv ff-num">{postalCode.toUpperCase()}</span>
          <span className="ff-bookaddr__zipn">From your quote</span>
        </p>
      </div>
      {children ? <div className="ff-bookaddr__contact">{children}</div> : null}
    </fieldset>
  );
}

/** Errors for one booking address (company, street, city, state required). Keys: `${prefix}.<field>`. */
export function bookingAddressErrors(prefix: string, v: BookingAddressValue, side: "pickup" | "delivery"): Record<string, string> {
  const e: Record<string, string> = {};
  if (!v.companyName.trim()) e[`${prefix}.companyName`] = `Add the ${side === "pickup" ? "shipper" : "consignee"} company for the BOL.`;
  else if (v.companyName.trim().length > 120) e[`${prefix}.companyName`] = "120 characters max.";
  if (v.line1.trim().length < 3) e[`${prefix}.line1`] = `Enter the ${side} street address.`;
  else if (v.line1.trim().length > 200) e[`${prefix}.line1`] = "200 characters max.";
  if (v.line2.trim().length > 200) e[`${prefix}.line2`] = "200 characters max.";
  if (!v.city.trim()) e[`${prefix}.city`] = "Enter the city.";
  if (!/^[A-Za-z]{2}$/.test(v.region.trim())) e[`${prefix}.region`] = "Use the 2-letter state or province.";
  return e;
}
